import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Search, Mail, MapPin, Phone, Calendar, FileDown, Plus, AlertCircle, Archive, Loader2, ShieldCheck, Package, ExternalLink, RefreshCw, Bell, BellOff } from "lucide-react";
import { PortalShell } from "@/components/PortalShell";
import { CreateBookingModal } from "@/components/CreateBookingModal";
import { SendPayLinkButton } from "@/components/SendPayLinkButton";
import { ReconcileFromStripeDialog } from "@/components/ReconcileFromStripeDialog";
import { ShowTestDataToggle, useShowDemoToggle } from "@/components/ShowTestDataToggle";
import { ArchiveStatusFilter, useArchiveFilter } from "@/components/ArchiveStatusFilter";
import { ArchiveActionsMenu } from "@/components/ArchiveActionsMenu";
import { GhlNotificationStatus } from "@/components/GhlNotificationStatus";
import api, { formatApiError } from "@/lib/api";
import { formatDateAU } from "@/lib/formatDateAU";
// Phase 2L-A144-fu-A — shipping filter helpers (Feb 2026). See the
// module for the vocabulary-mapping rationale.
import {
  SHIPPING_BUCKETS,
  SHIPPING_BUCKET_MAP,
  shippingBucket,
  isAwaitingSampleReturn,
  shippingTrackingHref,
  shippingTrackingNumber,
} from "@/lib/shippingStatusFilters";
import { bookingTrackingLinks } from "@/lib/carrierTracking";
import { returnStateChip } from "@/lib/returnEta";
import { toast } from "sonner";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";

const STATUSES = [
  { value: "pending", label: "Pending", color: "bg-slate-100 text-slate-700" },
  { value: "paid", label: "Paid", color: "bg-emerald-50 text-emerald-700" },
  { value: "received", label: "Received", color: "bg-blue-50 text-blue-700" },
  { value: "processing", label: "Processing", color: "bg-amber-50 text-amber-700" },
  { value: "completed", label: "Completed", color: "bg-purple-50 text-purple-700" },
  { value: "cancelled", label: "Cancelled", color: "bg-rose-50 text-rose-700" },
];

const STATUS_MAP = Object.fromEntries(STATUSES.map((s) => [s.value, s]));

// Derived payment status — see /app/backend/routers/bookings.py for the
// fields that drive these flags. Calculated on the client because every
// field is already in the bookings list payload.
function paymentStatus(b) {
  if (b.status === "cancelled") {
    return { label: "Cancelled", color: "bg-rose-100 text-rose-700" };
  }
  // Phase 2L-A29 — explicit payment_status soft-states take precedence
  // over the legacy `status` field. These are the new badges shown when
  // a customer abandons checkout or starts a second booking.
  const ps = (b.payment_status || "").toLowerCase();
  if (ps === "abandoned") {
    return { label: "Abandoned", color: "bg-rose-100 text-rose-700",
             tip: "Stripe session expired or aged out without payment." };
  }
  if (ps === "superseded") {
    return { label: "Superseded", color: "bg-fuchsia-100 text-fuchsia-700",
             tip: `Replaced by a newer booking${b.superseded_by ? ` (${b.superseded_by})` : ""}.` };
  }
  if (ps === "failed") {
    return { label: "Payment failed", color: "bg-rose-100 text-rose-700",
             tip: "Stripe reported a hard payment failure." };
  }
  // Phase 2L-A33 — explicit "no payment required" admin/lab zero-charge.
  if (ps === "not_required" || b.no_payment_required === true) {
    return { label: "No payment required", color: "bg-slate-100 text-slate-600",
             tip: "Admin/Lab-created zero-charge booking — no payment is owed." };
  }
  if (b.is_paid || b.status === "paid" || ps === "paid") {
    return { label: "Paid", color: "bg-emerald-100 text-emerald-700" };
  }
  if (b.complimentary || b.payment_mode === "comp" || (b.final_amount_payable ?? b.total) === 0) {
    return { label: "No payment required", color: "bg-slate-100 text-slate-600" };
  }
  // Invoice bookings (payment_method=bank_transfer) — once the invoice
  // email has been sent the booking is awaiting a manual bank reconcile.
  if (b.payment_mode === "invoice" || b.payment_method === "bank_transfer") {
    if (b.invoice_sent_at) {
      return { label: "Invoice sent · awaiting payment", color: "bg-amber-100 text-amber-700" };
    }
    return { label: "Pending · awaiting invoice", color: "bg-slate-100 text-slate-700" };
  }
  // Look at audit log for the most recent pay-link-related action.
  const audits = b.audit_log || [];
  const lastPayAction = [...audits].reverse().find((a) =>
    ["pay_link_sent", "pay_link_generated", "pay_link_stripe_failed"].includes(a.action)
  );
  if (lastPayAction?.action === "pay_link_stripe_failed") {
    return { label: "Pay link failed — retry", color: "bg-rose-100 text-rose-700" };
  }
  if (b.payment_link_sent_at) {
    return { label: "Awaiting payment", color: "bg-amber-100 text-amber-700" };
  }
  // Phase 2L-A29 — public flow: customer reached Stripe but hasn't paid yet.
  if (ps === "pending") {
    return { label: "Awaiting payment", color: "bg-amber-100 text-amber-700",
             tip: "Stripe checkout session created — customer hasn't paid yet." };
  }
  return { label: "Pending · awaiting pay link", color: "bg-slate-100 text-slate-700" };
}

export default function AdminBookings() {
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");
  const [query, setQuery] = useState("");
  const [showCreate, setShowCreate] = useState(false);
  const [me, setMe] = useState(null);
  const [showDemo, setShowDemo] = useShowDemoToggle("admin.bookings.showDemo");
  const [archived, setArchived] = useArchiveFilter("admin.bookings.archived");

  // Phase 2L-A144-fu-A — shipping-status filter (Feb 2026). Persisted
  // in localStorage so returning admins land back on their last-used
  // triage view. `all` is the default so shipping filtering never
  // gets in the way of admins looking at non-posted-kit bookings.
  const [shippingFilter, setShippingFilter] = useState(() => {
    try {
      return localStorage.getItem("admin.bookings.shippingFilter") || "all";
    } catch { return "all"; }
  });
  useEffect(() => {
    try { localStorage.setItem("admin.bookings.shippingFilter", shippingFilter); }
    catch { /* localStorage unavailable — non-fatal */ }
  }, [shippingFilter]);

  // Feb 2026 — Kit method filter. Lets ops zero in on mail-out vs
  // drop-off vs own-bottle workload. Persisted alongside the shipping
  // filter so a returning admin lands back on the same triage view.
  const [kitMethodFilter, setKitMethodFilter] = useState(() => {
    try {
      return localStorage.getItem("admin.bookings.kitMethodFilter") || "all";
    } catch { return "all"; }
  });
  useEffect(() => {
    try { localStorage.setItem("admin.bookings.kitMethodFilter", kitMethodFilter); }
    catch { /* localStorage unavailable — non-fatal */ }
  }, [kitMethodFilter]);

  // Phase 2L-A41 — admin bulk archive selection state. Only used when
  // `me.role === "admin"`. Cleared automatically when the result set
  // changes (filter / archived / showDemo / search) so a stale Ref can
  // never sneak into a bulk action.
  const [selectedRefs, setSelectedRefs] = useState(() => new Set());
  const [bulkOpen, setBulkOpen] = useState(false);
  const [bulkBusy, setBulkBusy] = useState(false);
  const [reconcileOpen, setReconcileOpen] = useState(false);
  const [resyncing, setResyncing] = useState("");
  const isAdmin = me?.role === "admin";

  // Nov 2026 — "Disable/Enable payment reminders" action on the booking's
  // row menu. Lives here (not gated behind SendPayLinkButton's
  // isBookingChasable check) because the whole point of this toggle is to
  // stay reachable even for a booking the chase-payment surface has
  // already hidden (e.g. payment_status === "abandoned") — exactly the
  // case that made SCWT-CFCC22's reminder-suppression request impossible
  // to action from the UI.
  const [confirmReminderFor, setConfirmReminderFor] = useState(null);
  const [reminderBusy, setReminderBusy] = useState(false);

  const toggleReminders = async () => {
    if (!confirmReminderFor) return;
    const b = confirmReminderFor;
    const disable = !b.payment_reminders_disabled;
    setReminderBusy(true);
    try {
      await api.post(`/admin/bookings/${b.reference}/${disable ? "disable" : "enable"}-reminders`);
      toast.success(disable ? `Payment reminders disabled for ${b.reference}` : `Payment reminders re-enabled for ${b.reference}`);
      setConfirmReminderFor(null);
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Could not update reminders");
    } finally {
      setReminderBusy(false);
    }
  };


  const load = async (silent = false) => {
    // Jun 2026 — SCWT-BOOKING-PERF: the 30s auto-refresh used to flip
    // the whole table back to the loading spinner while it refetched.
    // Background refreshes now swap data in place; the spinner only
    // shows on first load / filter changes.
    if (!silent) setLoading(true);
    try {
      const params = new URLSearchParams();
      if (showDemo) params.set("show_demo", "true");
      if (archived !== "active") params.set("archived", archived);
      const qs = params.toString() ? `?${params.toString()}` : "";
      const { data } = await api.get(`/admin/bookings${qs}`);
      setBookings(data);
    } finally {
      if (!silent) setLoading(false);
    }
  };

  // Sep 2026 — clear a stale "Failed · needs action" from the triage list:
  // re-derives the pill from the labels actually on the booking.
  const resyncShipping = async (reference) => {
    setResyncing(reference);
    try {
      const { data } = await api.post(
        `/admin/bookings/${reference}/shipping/starshipit/resync-status`);
      toast[data.changed ? "success" : "info"](
        data.changed
          ? `${reference}: ${data.before || "failed"} → ${data.after}`
          : `${reference} is genuinely ${data.after}${
            data.legs?.outbound?.last_error ? ` — ${data.legs.outbound.last_error}` : ""}`);
      await load(true);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not re-check this booking");
    } finally {
      setResyncing("");
    }
  };


  useEffect(() => { load(); }, [showDemo, archived]);
  // Feb 2026 — auto-refresh the list every 30s so freshly-paid
  // bookings appear without an F5. Skips while a modal is open or
  // when the tab is backgrounded (browser pauses timers anyway, but
  // this saves a wasted request the moment the tab is refocused).
  useEffect(() => {
    const iv = setInterval(() => {
      if (document.visibilityState === "visible") {
        load(true);
      }
    }, 30_000);
    return () => clearInterval(iv);
  }, [showDemo, archived]);
  useEffect(() => {
    // Lightweight `me` lookup so the modal can lock down admin-only payment modes
    api.get("/auth/me").then(({ data }) => setMe(data)).catch(() => setMe(null));
  }, []);

  // Phase 2L-A6: after Create Booking succeeds, force the filter back to
  // Active. Without this the freshly-created (active) booking can be
  // invisible if the staff member previously toggled to Archived/All.
  // Then reload so the new booking appears at the top of the list.
  const onCreated = (created) => {
    if (archived !== "active") {
      setArchived("active");  // triggers a load() via the useEffect above
    } else {
      load();
    }
    if (created?.pay_link_result?.ok) {
      toast.success(
        `${created.reference} created · pay link sent to ${created.pay_link_result.customer_email}`,
      );
    } else if (created?.pay_link_result && !created.pay_link_result.ok) {
      toast.error(
        `${created.reference} created, but pay link failed: ${created.pay_link_result.error || "Stripe error"} · use Send Pay Link to retry`,
      );
    } else if (created?.invoice_result?.ok) {
      toast.success(
        `${created.reference} created · invoice sent to ${created.invoice_result.customer_email}`,
      );
    } else if (created?.invoice_result && !created.invoice_result.ok) {
      toast.error(
        `${created.reference} created, but invoice email failed · check Settings → bank details`,
      );
    }
  };

  const updateStatus = async (ref, status) => {
    try {
      await api.post(`/admin/bookings/${ref}/status`, { status });
      toast.success(`${ref} → ${STATUS_MAP[status].label}`);
      setBookings((bs) => bs.map((b) => (b.reference === ref ? { ...b, status } : b)));
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    }
  };

  const apiBase = process.env.REACT_APP_BACKEND_URL;
  const labelUrl = (ref) => `${apiBase}/api/bookings/${ref}/labels.pdf`;

  const counts = bookings.reduce((acc, b) => {
    acc[b.status] = (acc[b.status] || 0) + 1;
    return acc;
  }, {});

  // Feb 2026 — Kit-method chip counts. Derived from the full bookings
  // pool (before status/search/shipping filters) so switching between
  // methods never "loses" the control. Non-mail-out methods are still
  // valid slices even when their count is 0.
  const kitMethodCounts = useMemo(() => {
    const acc = { all: bookings.length, mail_out: 0, local_pickup_dropoff: 0, existing_bottle: 0, admin_supplied: 0, technician_collected: 0 };
    for (const b of bookings) {
      const k = (b.kit_method || "").toLowerCase();
      if (k in acc) acc[k] += 1;
    }
    return acc;
  }, [bookings]);

  // Phase 2L-A144-fu-A — derive the list AFTER the status filter and
  // search query have been applied but BEFORE the shipping filter is
  // applied. This is what both `shippingCounts` and `visible` build on.
  //
  //   * Deriving counts from this list (not from `bookings`) makes the
  //     'N posted kits in view' number honest and lets the strip
  //     auto-hide when the admin narrows to a status with no
  //     posted-kit rows (e.g. Cancelled).
  //   * Excluding the shipping filter from this intermediate list
  //     means clicking a chip does NOT wipe out the strip — the
  //     other chips + their counts remain visible so the admin can
  //     jump between buckets without ever "losing" the control.
  const preShippingVisible = useMemo(() => {
    const q = query.trim().toLowerCase();
    return bookings.filter((b) => {
      if (filter !== "all" && b.status !== filter) return false;
      if (kitMethodFilter !== "all"
          && (b.kit_method || "").toLowerCase() !== kitMethodFilter) return false;
      if (!q) return true;
      return (
        b.reference.toLowerCase().includes(q) ||
        b.customer?.email?.toLowerCase().includes(q) ||
        b.customer?.name?.toLowerCase().includes(q) ||
        // Also match tracking number when the admin pastes an AusPost id
        // into the search box (very common triage flow).
        (shippingTrackingNumber(b) || "").toLowerCase().includes(q)
      );
    });
  }, [bookings, filter, query, kitMethodFilter]);

  // Bucket counts for the shipping filter chips — derived from the
  // narrowed list above so the numbers stay in sync with what's on
  // screen. Only counts shipping-relevant bookings; non-posted-kit
  // rows never contribute — matches the "self-hides for lab-pickup"
  // contract on the BookingDetail panel.
  const shippingCounts = useMemo(() => {
    const acc = { total: 0 };
    for (const b of preShippingVisible) {
      const bucket = shippingBucket(b);
      if (!bucket) continue;
      acc.total += 1;
      acc[bucket] = (acc[bucket] || 0) + 1;
    }
    return acc;
  }, [preShippingVisible]);

  const visible = shippingFilter === "all"
    ? preShippingVisible
    : preShippingVisible.filter((b) => shippingBucket(b) === shippingFilter);

  // Phase 2L-A41 — derive everything from visible + selectedRefs so a
  // changing filter/query naturally hides selections that are no longer
  // in scope, without an extra useEffect doing setState mid-render.
  const visibleRefs = useMemo(() => visible.map((b) => b.reference), [visible]);
  const allVisibleSelected = visibleRefs.length > 0
    && visibleRefs.every((r) => selectedRefs.has(r));
  const someVisibleSelected = !allVisibleSelected
    && visibleRefs.some((r) => selectedRefs.has(r));
  const selectedBookings = visible.filter((b) => selectedRefs.has(b.reference));
  const selectedCount = selectedBookings.length;
  const selectedPaidCount = selectedBookings.filter((b) => b.is_paid).length;

  const toggleOne = (ref) => {
    setSelectedRefs((prev) => {
      const next = new Set(prev);
      if (next.has(ref)) next.delete(ref); else next.add(ref);
      return next;
    });
  };
  const toggleAllVisible = () => {
    setSelectedRefs((prev) => {
      if (allVisibleSelected) {
        const next = new Set(prev);
        for (const r of visibleRefs) next.delete(r);
        return next;
      }
      const next = new Set(prev);
      for (const r of visibleRefs) next.add(r);
      return next;
    });
  };
  const clearSelection = () => setSelectedRefs(new Set());

  const runBulkArchive = async () => {
    if (selectedCount === 0) return;
    setBulkBusy(true);
    try {
      const refs = selectedBookings.map((b) => b.reference);
      const { data } = await api.patch("/admin/bookings/bulk-archive", {
        refs,
        reason: `Bulk archive from admin list (${refs.length} bookings)`,
      });
      const bits = [];
      if (data.total_archived) bits.push(`${data.total_archived} archived`);
      if (data.already_archived?.length) bits.push(`${data.already_archived.length} already archived`);
      if (data.not_found?.length) bits.push(`${data.not_found.length} not found`);
      if (data.paid_archived_count) bits.push(`${data.paid_archived_count} paid`);
      toast.success(`Archive done · ${bits.join(" · ")}`);
      clearSelection();
      setBulkOpen(false);
      await load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    } finally {
      setBulkBusy(false);
    }
  };

  return (
    <PortalShell title="Bookings" subtitle="Admin" wide>
      {/* Phase 2L-A6: surface non-default archive filters so newly-created
          bookings aren't perceived as "missing" when they're really just
          filtered out. */}
      {archived !== "active" && (
        <div data-testid="admin-bookings-archive-banner" className="mb-4 flex items-start gap-3 rounded-2xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900">
          <AlertCircle className="w-4 h-4 mt-0.5 shrink-0" />
          <div className="flex-1">
            <div className="font-semibold">
              Showing {archived === "archived" ? "archived" : "all"} bookings.
            </div>
            <div className="text-xs text-amber-800/90 mt-0.5">
              New active bookings won&apos;t appear in this view —
              <button
                onClick={() => setArchived("active")}
                className="ml-1 font-semibold underline hover:text-amber-950"
                data-testid="admin-bookings-archive-banner-reset"
              >
                switch to Active
              </button>
              .
            </div>
          </div>
        </div>
      )}

      {/* Phase 2L-A144-fu-A — Shipping-filter active banner. Same
          pattern as the archive banner above — surfaces that the
          admin has narrowed the view so non-posted-kit bookings are
          intentionally hidden. */}
      {shippingFilter !== "all" && (
        <div
          data-testid="admin-bookings-shipping-banner"
          className="mb-4 flex items-start gap-3 rounded-2xl border border-cyan-200 bg-cyan-50 px-4 py-3 text-sm text-cyan-900"
        >
          <Package className="w-4 h-4 mt-0.5 shrink-0" />
          <div className="flex-1">
            <div className="font-semibold">
              Filtered to posted kits: {SHIPPING_BUCKET_MAP[shippingFilter]?.label || shippingFilter}.
            </div>
            <div className="text-xs text-cyan-800/90 mt-0.5">
              Non-posted-kit bookings are hidden while this filter is active —
              <button
                onClick={() => setShippingFilter("all")}
                className="ml-1 font-semibold underline hover:text-cyan-950"
                data-testid="admin-bookings-shipping-banner-reset"
              >
                clear shipping filter
              </button>
              .
            </div>
          </div>
        </div>
      )}

      {/* Header with Create button */}
      <div className="flex items-center justify-between mb-5">
        <h1 className="font-display text-2xl sm:text-3xl font-semibold text-slate-900">All bookings</h1>
        <div className="flex items-center gap-4">
          <ArchiveStatusFilter value={archived} onChange={setArchived} testId="bookings-archive-filter" />
          <ShowTestDataToggle showDemo={showDemo} onChange={setShowDemo} testId="bookings-show-demo-toggle" />
          {isAdmin && (
            <Button
              data-testid="admin-bookings-reconcile-stripe"
              onClick={() => setReconcileOpen(true)}
              variant="outline"
              className="rounded-full font-semibold"
              title="Reconcile a paid Stripe booking that's stuck as awaiting payment"
            >
              <ShieldCheck className="w-4 h-4 mr-1.5" /> Reconcile from Stripe
            </Button>
          )}
          <Button
            data-testid="admin-bookings-create"
            onClick={() => setShowCreate(true)}
            className="rounded-full font-semibold"
          >
            <Plus className="w-4 h-4 mr-1.5" /> Create booking
          </Button>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3 mb-7">
        {STATUSES.map((s) => (
          <button
            key={s.value}
            onClick={() => setFilter(s.value)}
            data-testid={`admin-bookings-filter-${s.value}`}
            className={`text-left p-4 rounded-2xl border transition-all ${
              filter === s.value
                ? "border-slate-900 bg-slate-900 text-white"
                : "border-slate-200 bg-white hover:border-slate-300"
            }`}
          >
            <div className={`text-[10px] uppercase tracking-wider font-bold ${filter === s.value ? "text-cyan-400" : "text-slate-500"}`}>{s.label}</div>
            <div className={`font-display text-2xl font-bold mt-1 ${filter === s.value ? "text-white" : "text-slate-900"}`}>{counts[s.value] || 0}</div>
          </button>
        ))}
      </div>

      {/* Phase 2L-A144-fu-A — Shipping-status filter strip. Only
          renders when at least one booking in the current view has
          shipping state, so admins on a fresh install / accounts with
          no posted-kit bookings yet don't see empty chips. Bucket
          semantics are defined in `/app/frontend/src/lib/shippingStatusFilters.js`. */}
      {shippingCounts.total > 0 && (
        <div
          data-testid="admin-bookings-shipping-filter-strip"
          className="mb-7 rounded-2xl border border-slate-200 bg-white p-4"
        >
          <div className="mb-3 flex items-center gap-2 text-[11px] uppercase tracking-wider font-bold text-slate-500">
            <Package className="w-3.5 h-3.5 text-cyan-600" />
            Filter by posted-kit shipping status
            <span
              data-testid="admin-bookings-shipping-total"
              className="ml-2 normal-case tracking-normal font-semibold text-slate-400"
            >
              {shippingCounts.total} posted {shippingCounts.total === 1 ? "kit" : "kits"} in view
            </span>
          </div>
          <div className="flex flex-wrap gap-2">
            <button
              onClick={() => setShippingFilter("all")}
              data-testid="admin-bookings-shipping-filter-all"
              className={`inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full text-xs font-semibold transition-colors ${
                shippingFilter === "all"
                  ? "bg-slate-900 text-white"
                  : "bg-slate-100 text-slate-700 hover:bg-slate-200"
              }`}
            >
              All shipping
              <span className={`inline-flex items-center justify-center min-w-[20px] h-5 px-1.5 rounded-full text-[10px] font-bold ${
                shippingFilter === "all" ? "bg-white/20 text-white" : "bg-white text-slate-600"
              }`}>{shippingCounts.total}</span>
            </button>
            {SHIPPING_BUCKETS.map((b) => {
              const active = shippingFilter === b.key;
              const count = shippingCounts[b.key] || 0;
              return (
                <button
                  key={b.key}
                  onClick={() => setShippingFilter(b.key)}
                  data-testid={`admin-bookings-shipping-filter-${b.key}`}
                  disabled={count === 0}
                  className={`inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full text-xs font-semibold transition-colors ${
                    active
                      ? "bg-slate-900 text-white"
                      : count === 0
                        ? "bg-slate-50 text-slate-400 cursor-not-allowed"
                        : `${b.chipColor} hover:brightness-95`
                  }`}
                  title={count === 0 ? "No bookings in this bucket right now" : b.label}
                >
                  {b.label}
                  <span
                    data-testid={`admin-bookings-shipping-count-${b.key}`}
                    className={`inline-flex items-center justify-center min-w-[20px] h-5 px-1.5 rounded-full text-[10px] font-bold ${
                      active ? "bg-white/20 text-white" : "bg-white/70 text-slate-700"
                    }`}
                  >{count}</span>
                </button>
              );
            })}
          </div>
        </div>
      )}

      <div className="flex flex-col sm:flex-row gap-3 mb-5">
        <button
          onClick={() => setFilter("all")}
          className={`px-4 py-2 rounded-full text-sm font-semibold transition-colors ${
            filter === "all" ? "bg-cyan-500 text-white" : "bg-white border border-slate-200 text-slate-700"
          }`}
        >
          All ({bookings.length})
        </button>
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
          <Input
            data-testid="admin-bookings-search"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search reference, name or email"
            className="pl-10 h-11 rounded-full bg-white"
          />
        </div>
      </div>

      {/* Feb 2026 — Kit method filter chips. One-click slice by
          Mail-out / Drop-off / Own bottle / Admin kit. State persists
          in localStorage so returning admins land on their last view. */}
      <div className="flex flex-wrap items-center gap-2 mb-5" data-testid="admin-bookings-kit-method-filter">
        <span className="text-[10px] uppercase tracking-[0.15em] font-bold text-slate-500 mr-1">Kit method</span>
        {[
          { key: "all",                  label: "All",         tone: "bg-slate-800 text-white" },
          { key: "mail_out",             label: "Mail-out",    tone: "bg-amber-500 text-white" },
          { key: "local_pickup_dropoff", label: "Drop-off",    tone: "bg-cyan-600 text-white" },
          { key: "existing_bottle",      label: "Own bottle",  tone: "bg-slate-500 text-white" },
          { key: "admin_supplied",       label: "Admin kit",   tone: "bg-slate-500 text-white" },
          { key: "technician_collected", label: "Tech collect", tone: "bg-indigo-600 text-white" },
        ].map((b) => {
          const active = kitMethodFilter === b.key;
          const count  = kitMethodCounts[b.key] ?? 0;
          return (
            <button
              key={b.key}
              type="button"
              onClick={() => setKitMethodFilter(b.key)}
              data-testid={`admin-bookings-kit-method-chip-${b.key}`}
              className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-xs font-semibold transition-colors ${
                active ? b.tone : "bg-white border border-slate-200 text-slate-700 hover:border-slate-300"
              }`}
            >
              {b.label}
              <span
                className={`inline-flex items-center justify-center min-w-[20px] h-5 px-1.5 rounded-full text-[10px] font-bold ${
                  active ? "bg-white/20 text-white" : "bg-slate-100 text-slate-700"
                }`}
              >{count}</span>
            </button>
          );
        })}
      </div>

      <div data-testid="admin-bookings-table" className="rounded-3xl bg-white border border-slate-200 overflow-hidden">
        {isAdmin && selectedCount > 0 && (
          <div
            data-testid="bulk-action-bar"
            className="flex flex-wrap items-center justify-between gap-3 bg-cyan-50 border-b border-cyan-200 px-4 py-3"
          >
            <div className="text-sm font-semibold text-cyan-900">
              <span data-testid="bulk-selected-count">{selectedCount}</span> selected
              {selectedPaidCount > 0 && (
                <span
                  data-testid="bulk-selected-paid-count"
                  className="ml-2 inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-amber-100 text-amber-800 text-[11px]"
                >
                  <AlertCircle className="w-3 h-3" /> {selectedPaidCount} paid
                </span>
              )}
            </div>
            <div className="flex items-center gap-2">
              <Button
                data-testid="bulk-clear-selection"
                variant="ghost"
                size="sm"
                onClick={clearSelection}
                className="h-8"
              >
                Clear selection
              </Button>
              <Button
                data-testid="bulk-archive-button"
                onClick={() => setBulkOpen(true)}
                size="sm"
                className="h-8 bg-slate-900 hover:bg-slate-800 text-white"
              >
                <Archive className="w-4 h-4 mr-1.5" />
                Archive selected
              </Button>
            </div>
          </div>
        )}
        {loading ? (
          <div className="p-10 text-center text-slate-500">Loading bookings...</div>
        ) : visible.length === 0 ? (
          <div className="p-10 text-center text-slate-500">No bookings found.</div>
        ) : (
          <div className="overflow-x-auto">
            {/* Feb 2026 — Feb 27 update: outer PortalShell now runs at
                max-w-[1600px] for this page (via `wide` prop), so the
                table fits natively on typical laptop screens. Kept a
                gentle min-w-[1200px] so narrower viewports still get a
                horizontal scrollbar rather than crushed columns. */}
            <table className="w-full min-w-[1200px] text-sm">
              <thead className="bg-slate-50">
                <tr>
                  {isAdmin && (
                    <th className="text-left p-4 w-10">
                      <Checkbox
                        data-testid="bulk-select-all"
                        checked={allVisibleSelected ? true : (someVisibleSelected ? "indeterminate" : false)}
                        onCheckedChange={toggleAllVisible}
                        aria-label="Select all visible bookings"
                      />
                    </th>
                  )}
                  <th className="text-left p-4 text-[11px] uppercase tracking-wider font-bold text-slate-500">Reference</th>
                  <th className="text-left p-4 text-[11px] uppercase tracking-wider font-bold text-slate-500">Customer</th>
                  <th className="text-left p-4 text-[11px] uppercase tracking-wider font-bold text-slate-500">Test</th>
                  <th className="text-left p-4 text-[11px] uppercase tracking-wider font-bold text-slate-500">Total</th>
                  <th className="text-left p-4 text-[11px] uppercase tracking-wider font-bold text-slate-500">Created</th>
                  <th className="text-left p-4 text-[11px] uppercase tracking-wider font-bold text-slate-500">Status</th>
                  {/* Phase 2L-A144-fu-A — Shipping column. Only renders
                      when there's at least one shipping-relevant row in
                      view; otherwise the header stays hidden so the
                      table doesn't grow an empty column for accounts
                      with no posted-kit bookings yet. */}
                  {shippingCounts.total > 0 && (
                    <th
                      data-testid="admin-bookings-shipping-column-header"
                      className="text-left p-4 text-[11px] uppercase tracking-wider font-bold text-slate-500"
                    >Shipping</th>
                  )}
                  <th className="text-right p-4 text-[11px] uppercase tracking-wider font-bold text-slate-500">Actions</th>
                </tr>
              </thead>
              <tbody>
                {visible.map((b) => {
                  const sty = STATUS_MAP[b.status] || STATUS_MAP.pending;
                  return (
                    <tr key={b.id} className="border-t border-slate-100 hover:bg-slate-50/50">
                      {isAdmin && (
                        <td className="p-4">
                          <Checkbox
                            data-testid={`bulk-select-${b.reference}`}
                            checked={selectedRefs.has(b.reference)}
                            onCheckedChange={() => toggleOne(b.reference)}
                            aria-label={`Select booking ${b.reference}`}
                          />
                        </td>
                      )}
                      <td className="p-4">
                        <Link
                          to={`/admin/bookings/${b.reference}`}
                          data-testid={`admin-booking-ref-link-${b.reference}`}
                          className="font-mono text-xs font-bold text-slate-900 hover:text-cyan-700 hover:underline"
                        >{b.reference}</Link>
                        {/* Expert Advice pill removed Feb-2026 — add-on retired. */}
                      </td>
                      <td className="p-4">
                        {b.customer?.email ? (
                          <Link
                            to={`/admin/customers/${encodeURIComponent(b.customer.email)}`}
                            data-testid={`admin-booking-customer-link-${b.reference}`}
                            className="font-semibold text-slate-900 hover:text-cyan-700 hover:underline"
                          >
                            {b.customer?.name}
                          </Link>
                        ) : (
                          <div className="font-semibold text-slate-900">{b.customer?.name}</div>
                        )}
                        <div className="text-xs text-slate-500 inline-flex items-center gap-1"><Mail className="w-3 h-3" /> {b.customer?.email}</div>
                        {b.customer?.mobile && (
                          <div className="text-xs text-slate-500 inline-flex items-center gap-1 mt-0.5" data-testid="booking-row-phone"><Phone className="w-3 h-3" /> {b.customer.mobile}</div>
                        )}
                        <div className="text-xs text-slate-400 inline-flex items-center gap-1 mt-0.5"><MapPin className="w-3 h-3" /> {b.customer?.address?.slice(0, 36)}</div>
                      </td>
                      <td className="p-4 text-slate-700">{b.test_name}</td>
                      <td className="p-4 font-semibold text-slate-900">${b.total.toFixed(2)}</td>
                      <td className="p-4 text-xs text-slate-500">
                        <span className="inline-flex items-center gap-1"><Calendar className="w-3 h-3" /> {formatDateAU(b.created_at)}</span>
                      </td>
                      <td className="p-4">
                        <div className="flex flex-col gap-1">
                          <span className={`inline-block self-start px-2.5 py-1 rounded-full text-xs font-semibold ${sty.color}`}>{sty.label}</span>
                          {(() => {
                            const ps = paymentStatus(b);
                            return (
                              <span
                                data-testid={`admin-booking-payment-status-${b.reference}`}
                                className={`inline-block self-start px-2 py-0.5 rounded-full text-[10px] font-semibold ${ps.color}`}
                                title="Payment status"
                              >
                                {ps.label}
                              </span>
                            );
                          })()}
                          {(() => {
                            // Feb 2026 — Mail-out vs Drop-off pill. Lets
                            // ops spot at-a-glance which bookings need
                            // a Starshipit label vs which are drop-off
                            // (excluded from labels_to_create counts).
                            const km = (b.kit_method || "").toLowerCase();
                            if (!km) return null;
                            const short = km === "mail_out"             ? "Mail-out"
                                        : km === "local_pickup_dropoff" ? "Drop-off"
                                        : km === "existing_bottle"      ? "Own bottle"
                                        : km === "admin_supplied"       ? "Admin kit"
                                        : km;
                            const color = km === "mail_out"             ? "bg-amber-100 text-amber-800"
                                        : km === "local_pickup_dropoff" ? "bg-cyan-100 text-cyan-800"
                                        :                                 "bg-slate-100 text-slate-700";
                            return (
                              <span
                                data-testid={`admin-booking-kit-method-${b.reference}`}
                                className={`inline-block self-start px-2 py-0.5 rounded-full text-[10px] font-semibold ${color}`}
                                title="Sample kit method"
                              >
                                {short}
                              </span>
                            );
                          })()}
                          <GhlNotificationStatus booking={b} />
                        </div>
                      </td>
                      {/* Phase 2L-A144-fu-A — Shipping cell. Empty for
                          non-posted-kit bookings so the column reads
                          as "no shipping needed". Renders the bucket
                          pill + AusPost tracking number + a subtle
                          "sample not returned" hint when delivered. */}
                      {shippingCounts.total > 0 && (
                        <td className="p-4" data-testid={`admin-booking-shipping-cell-${b.reference}`}>
                          {(() => {
                            const bucket = shippingBucket(b);
                            if (!bucket) {
                              return <span className="text-[11px] text-slate-300">—</span>;
                            }
                            const cfg = SHIPPING_BUCKET_MAP[bucket];
                            const links = bookingTrackingLinks(b);
                            const awaitingReturn = isAwaitingSampleReturn(b);
                            return (
                              <div className="flex flex-col gap-1">
                                <span
                                  data-testid={`admin-booking-shipping-status-${b.reference}`}
                                  title={b.shipping_last_error || cfg.label}
                                  className={`inline-block self-start px-2.5 py-1 rounded-full text-[11px] font-semibold ${cfg.pillColor}`}
                                >{cfg.label}</span>
                                {/* Sep 2026 — a row can sit on a stale
                                    failure after the consignments came
                                    through. Re-derive it from the labels
                                    on file, right here in triage. */}
                                {bucket === "failed" && (
                                  <button
                                    type="button"
                                    disabled={resyncing === b.reference}
                                    onClick={(e) => { e.stopPropagation(); resyncShipping(b.reference); }}
                                    data-testid={`admin-booking-shipping-resync-${b.reference}`}
                                    title="Re-check this booking's labels and clear the failure if they're fine"
                                    className="inline-flex items-center gap-1 self-start rounded-full border border-rose-200 bg-white px-2 py-0.5 text-[10px] font-bold text-rose-700 hover:bg-rose-50 disabled:opacity-50"
                                  >
                                    <RefreshCw className={`w-2.5 h-2.5 ${resyncing === b.reference ? "animate-spin" : ""}`} />
                                    Re-check
                                  </button>
                                )}
                                {/* Sep 2026 — one row per label, each opening
                                    ITS OWN carrier (kit = Aramex, sample
                                    return = AusPost Express). Unknown carrier
                                    shows the number without a false link. */}
                                {links.map((l) => (l.href ? (
                                  <a
                                    key={l.kind}
                                    href={l.href}
                                    target="_blank"
                                    rel="noreferrer"
                                    onClick={(e) => e.stopPropagation()}
                                    data-testid={`admin-booking-shipping-tracking-${l.kind}-${b.reference}`}
                                    className="inline-flex items-center gap-1 text-[11px] font-mono text-cyan-700 hover:text-cyan-900 hover:underline"
                                    title={l.title}
                                  >
                                    <ExternalLink className="w-3 h-3 shrink-0" />
                                    <span className="truncate max-w-[130px]">{l.number}</span>
                                    <span className="font-sans text-[10px] text-slate-400">{l.carrierShortLabel}</span>
                                  </a>
                                ) : (
                                  <span
                                    key={l.kind}
                                    data-testid={`admin-booking-shipping-tracking-${l.kind}-${b.reference}`}
                                    className="inline-flex items-center gap-1 text-[11px] font-mono text-slate-500"
                                    title={l.title}
                                  >
                                    <span className="truncate max-w-[140px]">{l.number}</span>
                                  </span>
                                )))}
                                {/* Sep 2026 — sample-return state comes from
                                    the server's canonical helper; a created
                                    return label is NOT a posted sample. */}
                                {(() => {
                                  const chip = returnStateChip(b);
                                  if (!chip) return null;
                                  if (chip.kind === "overdue") {
                                    return (
                                      <span
                                        data-testid={`admin-booking-sample-overdue-${b.reference}`}
                                        title={chip.title}
                                        className="inline-block self-start rounded-full border border-rose-200 bg-rose-50 px-2 py-0.5 text-[10px] font-bold text-rose-700"
                                      >{chip.text}</span>
                                    );
                                  }
                                  if (chip.kind === "awaiting") {
                                    return (
                                      <span
                                        data-testid={`admin-booking-awaiting-return-${b.reference}`}
                                        title={chip.title}
                                        className="inline-block self-start rounded-full border border-amber-200 bg-amber-50 px-2 py-0.5 text-[10px] font-semibold text-amber-800"
                                      >{chip.text}</span>
                                    );
                                  }
                                  return (
                                    <span
                                      data-testid={`admin-booking-sample-eta-${b.reference}`}
                                      title={chip.title}
                                      className="text-[10px] text-slate-500"
                                    >{chip.text}</span>
                                  );
                                })()}
                                {awaitingReturn && (
                                  <span
                                    data-testid={`admin-booking-shipping-awaiting-return-${b.reference}`}
                                    className="inline-flex items-center gap-1 self-start px-2 py-0.5 rounded-full text-[10px] font-semibold bg-amber-50 text-amber-700 border border-amber-200"
                                    title="Kit delivered but no sample received back at the lab yet"
                                  >
                                    Sample not returned
                                  </span>
                                )}
                              </div>
                            );
                          })()}
                        </td>
                      )}
                      <td className="p-4">
                        <div className="flex items-center justify-end gap-2">
                          {!!b.payment_reminders_disabled && (
                            <span
                              data-testid={`admin-booking-reminders-disabled-${b.reference}`}
                              title="Payment reminders are switched off for this booking — the reminder scheduler will not contact this customer"
                              className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold bg-slate-100 text-slate-600 border border-slate-200 whitespace-nowrap"
                            >
                              <BellOff className="w-3 h-3" />
                              Reminders off
                            </span>
                          )}
                          <SendPayLinkButton booking={b} onSent={load} />
                          <Select value={b.status} onValueChange={(v) => updateStatus(b.reference, v)}>
                            <SelectTrigger data-testid={`admin-booking-status-${b.reference}`} className="h-8 w-28 text-xs rounded-lg">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              {STATUSES.map((s) => (
                                <SelectItem key={s.value} value={s.value} className="text-xs">{s.label}</SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                          <a
                            href={labelUrl(b.reference)}
                            target="_blank"
                            rel="noreferrer"
                            data-testid={`admin-booking-pdf-${b.reference}`}
                            className="inline-flex items-center justify-center w-8 h-8 rounded-lg bg-slate-100 hover:bg-slate-900 hover:text-white text-slate-600 transition-colors"
                            title="Download labels PDF"
                          >
                            <FileDown className="w-4 h-4" />
                          </a>
                          <ArchiveActionsMenu
                            recordType="booking"
                            recordId={b.id}
                            recordDisplay={b.reference}
                            isArchived={!!b.archived_at}
                            onChanged={load}
                            hidden={me?.role && me.role !== "admin"}
                            extraItems={[{
                              testId: `row-toggle-reminders-${b.reference}`,
                              icon: b.payment_reminders_disabled ? Bell : BellOff,
                              label: b.payment_reminders_disabled ? "Enable payment reminders" : "Disable payment reminders",
                              onClick: () => setConfirmReminderFor(b),
                            }]}
                          />
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
      <CreateBookingModal
        open={showCreate}
        onClose={() => setShowCreate(false)}
        currentUser={me}
        onCreated={onCreated}
      />

      {/* Phase 2L-A54 — Stripe-verified reconcile (admin only). */}
      <ReconcileFromStripeDialog
        open={reconcileOpen}
        onClose={() => setReconcileOpen(false)}
        onApplied={load}
      />

      {/* Phase 2L-A41 — Bulk archive confirmation. Admin only.
          Surfaces the paid-count so the user can't accidentally archive
          paid bookings without noticing. Archive is soft — payment
          records, reports, audit history, and timeline are preserved. */}
      <AlertDialog open={bulkOpen} onOpenChange={setBulkOpen}>
        <AlertDialogContent data-testid="bulk-archive-confirm">
          <AlertDialogHeader>
            <AlertDialogTitle>Archive {selectedCount} booking{selectedCount === 1 ? "" : "s"}?</AlertDialogTitle>
            <AlertDialogDescription className="space-y-2">
              <span className="block">
                You are about to archive {selectedCount} selected booking
                {selectedCount === 1 ? "" : "s"}. Archived bookings will be
                removed from normal admin/lab queues but can still be viewed
                via the archive filter.
              </span>
              {selectedPaidCount > 0 && (
                <span
                  data-testid="bulk-archive-paid-warning"
                  className="block px-3 py-2 rounded-lg bg-amber-50 border border-amber-200 text-amber-900 text-xs font-semibold"
                >
                  ⚠ {selectedPaidCount} of these {selectedPaidCount === 1 ? "is" : "are"} a paid booking.
                  Payment records, reports, audit history, and email logs are
                  preserved — archive does not delete any of these. Continue
                  only if you intend to hide {selectedPaidCount === 1 ? "this paid booking" : "these paid bookings"} from
                  active queues.
                </span>
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="bulk-archive-cancel" disabled={bulkBusy}>
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              data-testid="bulk-archive-confirm-button"
              onClick={runBulkArchive}
              disabled={bulkBusy}
              className="bg-slate-900 hover:bg-slate-800"
            >
              {bulkBusy
                ? <><Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> Archiving…</>
                : <><Archive className="w-4 h-4 mr-1.5" /> Archive {selectedCount}</>}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Nov 2026 — Disable/Enable payment reminders confirmation. Only
          ever touches payment_reminders_disabled + an audit_log entry
          (see POST /admin/bookings/{ref}/disable-reminders) — no other
          booking field, no report/status change, no customer email. */}
      <AlertDialog open={!!confirmReminderFor} onOpenChange={(open) => !open && setConfirmReminderFor(null)}>
        <AlertDialogContent data-testid="confirm-toggle-reminders">
          <AlertDialogHeader>
            <AlertDialogTitle>
              {confirmReminderFor?.payment_reminders_disabled ? "Re-enable" : "Disable"} payment reminders for {confirmReminderFor?.reference}?
            </AlertDialogTitle>
            <AlertDialogDescription>
              {confirmReminderFor?.payment_reminders_disabled
                ? "The automated payment-reminder scheduler will be allowed to contact this customer again if the booking is still unpaid."
                : "The automated payment-reminder scheduler will stop sending this customer any further reminder emails. This only affects reminders — it does not change the booking's status, archive it, or affect any other notification."}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="confirm-toggle-reminders-cancel" disabled={reminderBusy}>
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              data-testid="confirm-toggle-reminders-confirm"
              onClick={toggleReminders}
              disabled={reminderBusy}
              className={confirmReminderFor?.payment_reminders_disabled ? "" : "bg-amber-600 hover:bg-amber-700"}
            >
              {reminderBusy
                ? <><Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> Saving…</>
                : (confirmReminderFor?.payment_reminders_disabled ? "Re-enable reminders" : "Disable reminders")}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </PortalShell>
  );
}
